import type { FighterId } from '@archon3d/core';
import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import type { AudioBus } from '../audio/audioBus';
import type { MusicSource } from '../audio/soundDirector';
import type { BattleFeed } from '../game/battleFeed';
import type { GameSession } from '../game/session';
import type { LookSettings } from '../ui/settings';
import type { ShellStore } from '../ui/shell';
import { createGameScene, type GameSceneHandle } from '../scenes/createGameScene';
import { createEngine } from './createEngine';

export interface SceneHostHandle {
  enterVr(): Promise<void>;
  exitVr(): Promise<void>;
  resetView(): void;
  /** Gives up the duel that is running. */
  /** A person gives up the running duel: the first person's side unless one is named. */
  forfeit(side?: FighterId): void;
  /** A duel's outcome has been read: back to the board now. */
  continueOutcome(): void;
  /** Heavy smoke over the board, a game setting. */
  setFog(enabled: boolean): void;
  setFogDensity(density: number): void;
  /** Graphics, VR comfort, palette and tablet lettering, applied live. */
  applyLook(look: LookSettings): void;
  /** Odds on attack targets and warnings under threatened pieces, a game setting. */
  setThreatHints(on: boolean): void;
}

interface Props {
  session: GameSession;
  /** Where the scene reports the duel that is playing, for the overlay. */
  feed: BattleFeed;
  /** Setup, server and VR state shown on the table's tablets. */
  shell: ShellStore;
  /** Where the scene's sounds play. */
  audio: AudioBus;
  /** The radio: the ambience gives way to a station and follows the Music slider. */
  music?: MusicSource;
  /** Called once the scene renders, with whether an immersive-vr session is available. */
  onReady?: (xrSupported: boolean) => void;
  /**
   * Called instead when the table cannot be built: no WebGL, or the scene failed while it was
   * made. The host is then unmounted, which releases the engine and its WebGL context.
   */
  onError?: (error: unknown) => void;
  ref?: Ref<SceneHostHandle>;
}

/** Owns the canvas and the Babylon engine; everything else in the UI is plain React. */
export function SceneHost({ session, feed, shell, audio, music, onReady, onError, ref }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<GameSceneHandle | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // A new game goes onto the same table: the engine, the room and the XR session stay.
  useEffect(() => {
    sceneRef.current?.setSession(session);
  }, [session]);

  useImperativeHandle(ref, () => ({
    enterVr: () =>
      sceneRef.current?.enterVr() ?? Promise.reject(new Error('The scene is still loading')),
    exitVr: () => sceneRef.current?.exitVr() ?? Promise.resolve(),
    resetView: () => sceneRef.current?.resetView(),
    forfeit: (side) => sceneRef.current?.forfeit(side),
    continueOutcome: () => sceneRef.current?.continueOutcome(),
    setFog: (enabled) => sceneRef.current?.setFog(enabled),
    setFogDensity: (density) => sceneRef.current?.setFogDensity(density),
    applyLook: (look) => sceneRef.current?.applyLook(look),
    setThreatHints: (on) => sceneRef.current?.setThreatHints(on),
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let disposed = false;
    let engine: ReturnType<typeof createEngine>;
    try {
      engine = createEngine(canvas);
    } catch (error) {
      // No WebGL: turned off, refused by policy, or a GPU the browser will not use.
      onErrorRef.current?.(error);
      return;
    }

    void createGameScene(engine, sessionRef.current, feed, shell, audio, music)
      .then((handle) => {
        if (disposed) {
          handle.dispose();
          return;
        }
        sceneRef.current = handle;
        handle.setSession(sessionRef.current);
        engine.runRenderLoop(() => handle.scene.render());
        window.__archon = {
          ...window.__archon,
          ready: true,
          scene: handle.scene,
          activeMeshes: () => handle.scene.getActiveMeshes().length,
          fps: () => engine.getFps(),
          loadModel: async (file: string, place?: { height: number; x: number; z: number }) => {
            const instance = await handle.models.instantiate(
              { file, boardHeight: place?.height ?? 0.09 },
              `debug-model-${Date.now()}`,
            );
            const result = {
              meshes: instance.meshes.length,
              animations: [...instance.animations.keys()],
            };
            // Kept on the table when asked, so a model can be looked at with its real textures.
            if (place) {
              instance.root.position.set(place.x, 0.86, place.z);
              instance.play('idle');
            } else instance.dispose();
            return result;
          },
          practiceFight: (pick) => handle.practiceFight(pick),
          forfeit: (side) => handle.forfeit(side),
          arenaFrame: () => handle.arenaFrame(),
          arenaDaylight: () => handle.arenaDaylight(),
          skipDuelClock: (seconds: number) => handle.skipDuelClock(seconds),
          setArenaSkill: (skill) => handle.setArenaSkill(skill),
          arenaSkill: () => handle.arenaSkill(),
          setFog: (enabled: boolean) => handle.setFog(enabled),
          setFogDensity: (density: number) => handle.setFogDensity(density),
          applyLook: (look: LookSettings) => handle.applyLook(look),
          look: () => handle.look(),
          setThreatHints: (on: boolean) => handle.setThreatHints(on),
          hints: () => handle.hints(),
          hover: () => handle.hover(),
          screenPointOf: (cell) => handle.screenPointOf(cell),
          padZone: () => handle.padZone(),
          battle: () => feed.getSnapshot(),
          tablets: () => handle.tablets.text(),
          // Tests with two software-rendered pages pause one while the other boots.
          setRendering: (on: boolean) => {
            engine.stopRenderLoop();
            if (on) engine.runRenderLoop(() => handle.scene.render());
          },
        };
        onReadyRef.current?.(handle.xrSupported);
      })
      // Before, a failure here left a black table under the menu and nothing said
      // (client-audio-engine:bugs#2).
      .catch((error: unknown) => {
        if (!disposed) onErrorRef.current?.(error);
      });

    const onResize = () => engine.resize();
    window.addEventListener('resize', onResize);
    return () => {
      disposed = true;
      window.removeEventListener('resize', onResize);
      engine.stopRenderLoop();
      sceneRef.current?.dispose();
      sceneRef.current = null;
      engine.dispose();
      feed.publish(null);
      if (window.__archon) window.__archon.ready = false;
    };
  }, [feed, shell, audio, music]);

  return <canvas ref={canvasRef} className="scene" aria-label="3D game board" />;
}
